import React, { Component } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { Icon, Button, Table, Loader, AsyncButton } from '@driftt/tide-core-legacy'
import './style.styl'
import { fetchIntegrationEvents, destroyIntegrationEvents, fetchPaginatedIntegrationEvents } from 'app/modules/integrations/actions'
import { getEvents, getPaginationQuery, isLoaded, csvRequestSent } from 'app/modules/integrations/selectors'
import moment from 'moment'
import magnifyingGlassSVG from 'images/svg/magnifying-glass.svg'
import Select from 'react-select-plus'
import { SingleDatePicker } from 'react-dates'
import BusinessExportModal from '../BusinessEventExportModal'
import { LOG_TEAM_STATEMENTS, LOG_PLAYBOOK_STATEMENTS, LOG_WIDGET_STATEMENTS, LOG_PASSWORD_STATEMENTS } from 'constants/TeamsConstants'

const momentFormat = 'MMM Do, YYYY  h:mm a'

const severityOptions = [
  { label: 'All', value: 'ALL' },
  { label: 'Info', value: 'INFO' },
  { label: 'Warn', value: 'WARN' },
  { label: 'Error', value: 'ERROR' },
  { label: 'Severe', value: 'SEVERE' }
]

const typeOptions = [
  { label: 'All', value: 'ALL' },
  { label: 'User', value: 'USER' },
  { label: 'Playbook', value: 'PLAYBOOK' },
  { label: 'Widget', value: 'WIDGET' },
  { label: 'Team', value: 'TEAM' },
  { label: 'Password', value: 'PASSWORD' },
]

const SeverityRowItem = ({ level }) => {
  switch (level) {
    case 'WARN':
      return <Icon name="warning-triangle" width={18} height={18}/>
    case 'ERROR':
    case 'SEVERE':
      return <Icon name="cancel-circle" width={18} height={18}/>
    default:
      return null
  }
}

class EventTable extends Component {


  constructor(props) {
    super(props)
    
    let search = window.location.search;
    let params = new URLSearchParams(search);
    let epochFromQueryParam = params.get('epoch');
    const fromEpoch = moment(epochFromQueryParam, "x")
    const startTime = !!epochFromQueryParam ? { label: this.formatMoment(fromEpoch), value: 'epoch'} : { label: this.formatMoment(moment()), value: 'now' }
    const startDate = !!epochFromQueryParam ? fromEpoch : moment().startOf('day') 

    this.state = {
      loaded: false,
      severityFilter: 'ALL',
      referenceToFilter: 'ALL',
      typeFilter: 'ALL',
      timeFrame: '1',
      startTime: startTime,
      startDate: startDate,
      exportModalOpen: false
    }
  }

  componentDidMount() {
    this.getData()
  }

  componentWillUnmount() {
    this.props.actions.destroyIntegrationEvents(this.props.sourceId)
  }

  formatMoment = moment => moment.format('h:mma')

  getMoreData = () => {
    this.props.actions.fetchPaginatedIntegrationEvents({ sourceId: this.props.sourceId, queryStr: this.props.paginationQuery })
    return new Promise((res) => this.setState({ paginationResolve: res }))
  }

  getData = () => {
    const { sourceId } = this.props
    const { timeFrame, startTime, startDate } = this.state

    const baseTime = moment(startDate)

    if (startTime.value === 'now') {
      const now = moment()
      baseTime.hours(now.hours())
      baseTime.minutes(now.minutes())
    } else if (startTime.value === 'epoch') {
      baseTime.hours(startTime)
    }
    else  {
      baseTime.hours(startTime.value)
      baseTime.startOf('hour')
    }

    // Capture end of minute for inclusive query.
    baseTime.endOf('minute')

    const endTime = baseTime.unix()
    const start = baseTime.subtract(timeFrame, 'hours').unix()
    const offset = start - endTime

    const query = {
      sourceId,
      unit: 'seconds',
      start: endTime * 1000,
      offset: offset
    }

    this.props.actions.fetchIntegrationEvents(query)
    this.setState({ loaded: false })
  }
  
  createTableColumns = () => ([
    { label: '', width: '50px' },
    { label: 'TIME', width: '200px' },
    { label: 'EVENT', width: '70%' }
  ])
  
  componentWillReceiveProps(nextProps) {
    if (nextProps.isLoaded) {
      if (!this.state.loaded) {
        this.setState({ loaded: true })
      }
  
      if (this.state.paginationResolve) {
        this.state.paginationResolve()
        this.setState({ paginationResolve: null })
      }
    }
  }

  createTableRow = item => ({
    className: `event-table-row--${item.level.toLowerCase()}`,
    data: [
      { content: <span><SeverityRowItem level={item.level}/></span> },
      { content: <p>{moment(item.timestamp).format(momentFormat)}</p> },
      { content: <p>{item.message}</p> }
    ]
  })

  onSeverityFilterChange = filter => this.setState({ severityFilter: filter ? filter.value : 'ALL' })

  onTypeFilterChange = filter => this.setState({ typeFilter: filter ? filter.value : 'ALL' })

  onReferenceToFilterChange = filter => this.setState({ referenceToFilter: filter ? filter.value : 'ALL' })

  filterEvents = (events = []) => {
    const eventsFilteredBySeverity = this.filterEventsBySeverity(events)
    const eventsFilteredBySeverityAndType = this.filterEventsByType(eventsFilteredBySeverity)
    return this.filterEventsByReferenceTo(eventsFilteredBySeverityAndType)
  }

  getEventTypeFromMessage = (message) => {
    if(this.messageMatchesStatements(message, LOG_WIDGET_STATEMENTS)){
      return 'WIDGET';
    }
    else if(this.messageMatchesStatements(message, LOG_PASSWORD_STATEMENTS)){
      return 'PASSWORD'
    }
    else if(this.messageMatchesStatements(message, LOG_PLAYBOOK_STATEMENTS)){
      return 'PLAYBOOK'
    }
    else if(this.messageMatchesStatements(message, LOG_TEAM_STATEMENTS)){
      return 'TEAM'
    }
    else {
      return 'USER'
    }
  }

  messageMatchesStatements = (message, statements) => (
    statements.some((statement) => message.match(statement))
  )

  filterEventsByType = (events = []) => {
    const { typeFilter } = this.state

    return typeFilter === 'ALL'
      ? events
      : events.filter(({ message }) => this.getEventTypeFromMessage(message) === typeFilter)
  }

  filterEventsBySeverity = (events = []) => {
    const { severityFilter } = this.state

    return severityFilter === 'ALL'
      ? events
      : events.filter(({ level }) => level === severityFilter)
  }

  filterEventsByReferenceTo = (events = []) => {
    const { referenceToFilter } = this.state

    return referenceToFilter === 'ALL'
      ? events
      : events.filter(({ referenceId }) => referenceId === referenceToFilter)
  }

  onTimeFrameChange = timeFrame => this.setState({ timeFrame: timeFrame ? timeFrame.value : '1' }, this.getData)


  createHourOptions = () => {
    const hourOptions = []
    let hrIterator = moment().startOf('day')
    const iteratorEnd = moment().endOf('day')
    const now = moment()

    while (hrIterator < iteratorEnd){
      hourOptions.push({ value: hrIterator.hour(), label: this.formatMoment(hrIterator) })
      
      const next = moment(hrIterator).add(1, 'hour')
      if (hrIterator < now && now < next) {
        hourOptions.push({ value: 'now', label: this.formatMoment(now) })
      }

      hrIterator = next
    }

    return hourOptions
  }

  onDateChange = startDate => this.setState({ startDate }, this.getData)

  onStartTimeChange = startTime => this.setState({ startTime }, this.getData)

  onPickerFocusChange = () => this.setState(prevState => ({ pickerFocused: !prevState.pickerFocused }))
  
  openExportModal = () => {
    this.setState({
      exportModalOpen: true,
    })
  }

  closeExportModal = () => {
    this.setState({
      exportModalOpen: false,
    })
  }

  typeFilter = () => {
    return (
      <div className="event-log-type-filter">
        <p className="filter-log-copy">Type</p>
        <Select
          className="event-log-selector type-select"
          value={this.state.typeFilter}
          options={typeOptions}
          onChange={this.onTypeFilterChange}
          clearable={false}
        />
      </div>
    )
  }

  severityFilter = () => {
    return (
      <div className="event-log-severity-filter">
        <p className="filter-log-copy">Show</p>
        <Select
          className="event-log-selector type-select"
          value={this.state.severityFilter}
          options={severityOptions}
          onChange={this.onSeverityFilterChange}
          clearable={false}
        />
      </div>
    )
  }

  referenceToFilter = () => {
    const { referenceToFilterOptions } = this.props
    return (
      <div className="event-log-reference-filter">
        <p className="filter-log-copy">Filter by</p>
        <Select
          className="event-log-selector type-select"
          value={this.state.referenceToFilter}
          options={referenceToFilterOptions}
          onChange={this.onReferenceToFilterChange}
          clearable={false}
        />
      </div>
    )
  }

  renderFilterArea = () => (
    <div className="event-log-filter">
        {!this.props.hideSeverityFilter && this.severityFilter()}
        {this.props.showReferenceToFilter && this.referenceToFilter()}
        {this.props.showTypeFilter && this.typeFilter()}
        <p className="filter-log-copy"> activity from the</p>
          <Select
            className="event-log-selector timeframe-select"
            value={this.state.timeFrame}
            options={[
              { label: 'Previous Hour', value: '1' },
              { label: 'Previous 6 Hours', value: '6' },
              { label: 'Previous 12 Hours', value: '12' },
              { label: 'Previous 24 Hours', value: '24' }
            ]}
            onChange={this.onTimeFrameChange}
            clearable={false}
          />
          <p className="filter-log-copy"> starting on</p>
          <SingleDatePicker
            date={this.state.startDate}
            onDateChange={this.onDateChange}
            focused={this.state.pickerFocused}
            onFocusChange={this.onPickerFocusChange}
            numberOfMonths={1}
            isOutsideRange={date => date.startOf('day') > moment() || date.isBefore(moment().subtract(60, 'day'))}
        />
          <p className="filter-log-copy"> at</p>
          <Select
            className="event-log-selector hour-select"
            value={this.state.startTime}
            onChange={this.onStartTimeChange}
            options={this.createHourOptions()}
            clearable={false}
          />
          <Button type="tertiary" onClick={this.openExportModal}>Export CSV</Button>
        </div>
  )

  renderLoadedState = () => {
    const events = this.filterEvents(this.props.events)

    return (
      <div>
        <Table
          className="event-table"
          columns={this.createTableColumns()}
          data={events.map(this.createTableRow)}
        />
        { events && !events.size && (
          <div className="event-table-empty-state">
            <img src={magnifyingGlassSVG}/>
            <p> Hmmm... there’s nothing here. Try expanding your timeframe to see data in this log! </p>
          </div>
        )}
        { this.props.paginationQuery && (
          <AsyncButton
            delay={1}
            className="get-paginated-events-button" 
            type="secondary"
            promise={this.getMoreData}
          >
            Load More Events 
          </AsyncButton>
        )}
      </div>
    )
  }

  render() {
    return (
      <div className="event-table-wrapper">
      {this.renderFilterArea()}
      { this.state.loaded
        ? this.renderLoadedState() 
        : <Loader/> 
      }
      <BusinessExportModal 
      open={this.state.exportModalOpen} 
      onClose={this.closeExportModal}
      sourceId={this.props.sourceId} />
      </div>
    )
  }
}

const mapStateToProps = (state, props) => {
  return {
    events: getEvents(state, props.sourceId),
    paginationQuery: getPaginationQuery(state, props.sourceId),
    isLoaded : isLoaded(state, props.sourceId),
    csvRequestSent: csvRequestSent(state, props.sourceId)
  }
}

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    fetchIntegrationEvents,
    destroyIntegrationEvents,
    fetchPaginatedIntegrationEvents
  }, dispatch)
})

export default connect(mapStateToProps, mapDispatchToProps, null)(EventTable)


// WEBPACK FOOTER //
// ./src/scripts/components/Integration/components/EventTable/index.js